/**
 * IncidentsPanel - Baobab Connect
 * ================================
 * Composant pour déclarer et gérer les incidents d'une opération
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { 
  AlertTriangle, Plus, MessageSquare, Camera, X, 
  Clock, CheckCircle, AlertCircle, Send, ChevronDown, ChevronUp,
  MapPin, FileText, Trash2, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Couleurs par sévérité
const SEVERITY_STYLES = {
  faible: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
  moyenne: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  elevee: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  critique: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' }
};

const STATUS_STYLES = {
  ouvert: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle },
  en_cours: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  resolu: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  clos: { bg: 'bg-gray-100', text: 'text-gray-700', icon: CheckCircle }
};

const IncidentsPanel = ({ operationId, operationReference, token }) => {
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState({ total: 0, ouverts: 0, en_cours: 0, resolus: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [incidentConfig, setIncidentConfig] = useState({ types: [], severities: [], statuses: [] });
  const [expandedIncident, setExpandedIncident] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Formulaire de création
  const [formData, setFormData] = useState({
    incident_type: '',
    severity: 'moyenne',
    cause: '',
    description: '',
    location: ''
  });

  // Charger la configuration des types
  const loadConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/incidents/config/types`);
      setIncidentConfig(response.data);
    } catch (error) {
      console.error('Erreur chargement config:', error);
    }
  }, []);

  // Charger les incidents de l'opération
  const loadIncidents = useCallback(async () => {
    if (!operationId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/incidents?operation_id=${operationId}`, {});
      setIncidents(response.data.incidents || []);
      // Calculate stats from incidents
      const incidentList = response.data.incidents || [];
      const calculatedStats = {
        total: incidentList.length,
        ouverts: incidentList.filter(i => i.status === 'nouveau').length,
        en_cours: incidentList.filter(i => ['en_cours', 'en_attente_client', 'en_attente_interne'].includes(i.status)).length,
        resolus: incidentList.filter(i => ['resolu', 'clos'].includes(i.status)).length
      };
      setStats(calculatedStats);
    } catch (error) {
      console.error('Erreur chargement incidents:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les incidents",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [operationId, token]);

  useEffect(() => {
    loadConfig();
    loadIncidents();
  }, [loadConfig, loadIncidents]);

  // Créer un incident
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.incident_type || !formData.cause || formData.cause.length < 10) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires (cause min. 10 caractères)",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/incidents`, {
        operation_id: operationId,
        ...formData
      }, {});
      
      toast({
        title: "Incident déclaré",
        description: `Incident ${response.data.incident_id} créé avec succès`,
      });
      
      setShowForm(false);
      setFormData({ incident_type: '', severity: 'moyenne', cause: '', description: '', location: '' });
      loadIncidents();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de créer l'incident",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Mettre à jour le statut d'un incident
  const updateIncidentStatus = async (incidentId, newStatus, resolution = null) => {
    try {
      const updateData = { status: newStatus };
      if (resolution) updateData.resolution = resolution;
      
      await axios.put(`${API}/incidents/${incidentId}`, updateData, {});
      
      toast({ title: "Statut mis à jour" });
      loadIncidents();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le statut",
        variant: "destructive"
      });
    }
  };

  // Ajouter un commentaire
  const addComment = async (incidentId) => {
    if (!newComment.trim()) return;
    
    try {
      await axios.post(`${API}/incidents/${incidentId}/comments`, {
        content: newComment
      }, {});
      
      setNewComment('');
      loadIncidents();
      toast({ title: "Commentaire ajouté" });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter le commentaire",
        variant: "destructive"
      });
    }
  };

  const getSeverityStyle = (severity) => SEVERITY_STYLES[severity] || SEVERITY_STYLES.moyenne;
  const getStatusStyle = (status) => STATUS_STYLES[status] || STATUS_STYLES.ouvert;

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Incidents
              {stats.ouverts > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {stats.ouverts} ouvert{stats.ouverts > 1 ? 's' : ''}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              {stats.total === 0 
                ? "Aucun incident déclaré" 
                : `${stats.total} incident(s) - ${stats.resolus} résolu(s)`
              }
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadIncidents}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              size="sm"
              onClick={() => setShowForm(!showForm)}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Plus className="h-4 w-4 mr-1" />
              Déclarer
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Formulaire de création */}
        {showForm && (
          <form onSubmit={handleSubmit} className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium text-amber-800">Nouvel incident</h4>
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Type d&apos;incident *</label>
                <Select
                  value={formData.incident_type}
                  onValueChange={(value) => setFormData({ ...formData, incident_type: value })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {incidentConfig.types.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Sévérité</label>
                <Select
                  value={formData.severity}
                  onValueChange={(value) => setFormData({ ...formData, severity: value })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {incidentConfig.severities.map((sev) => (
                      <SelectItem key={sev.value} value={sev.value}>
                        <span className="flex items-center gap-2">
                          <span 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: sev.color }}
                          />
                          {sev.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600">Cause * (min. 10 caractères)</label>
              <Textarea
                value={formData.cause}
                onChange={(e) => setFormData({ ...formData, cause: e.target.value })}
                placeholder="Décrivez la cause de l'incident..."
                className="h-20"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600">Lieu (optionnel)</label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Ex: Route N1, KM 45"
                className="h-9"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                size="sm" 
                disabled={submitting}
                className="bg-amber-500 hover:bg-amber-600"
              >
                {submitting ? 'Création...' : 'Créer l\'incident'}
              </Button>
            </div>
          </form>
        )}

        {/* Liste des incidents */}
        {loading ? (
          <div className="text-center py-8 text-gray-500">
            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
            Chargement...
          </div>
        ) : incidents.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <AlertTriangle className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>Aucun incident déclaré pour cette opération</p>
          </div>
        ) : (
          <div className="space-y-3">
            {incidents.map((incident) => {
              const sevStyle = getSeverityStyle(incident.severity);
              const statStyle = getStatusStyle(incident.status);
              const isExpanded = expandedIncident === incident.incident_id;
              const StatusIcon = statStyle.icon;

              return (
                <div 
                  key={incident.incident_id} 
                  className={`border rounded-lg overflow-hidden ${sevStyle.border}`}
                >
                  {/* Header de l'incident */}
                  <div 
                    className={`p-3 cursor-pointer ${sevStyle.bg}`}
                    onClick={() => setExpandedIncident(isExpanded ? null : incident.incident_id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={`${sevStyle.text} ${sevStyle.bg}`}>
                            {incident.severity_info?.label || incident.severity}
                          </Badge>
                          <Badge className={`${statStyle.bg} ${statStyle.text} border-0`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {incident.status}
                          </Badge>
                          <span className="text-xs text-gray-500 font-mono">
                            {incident.incident_id}
                          </span>
                        </div>
                        <p className="font-medium text-sm text-gray-800">
                          {incident.incident_type_label || incident.incident_type}
                        </p>
                        <p className="text-xs text-gray-600 line-clamp-1">
                          {incident.cause}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {incident.comments?.length > 0 && (
                          <span className="flex items-center text-xs text-gray-500">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            {incident.comments.length}
                          </span>
                        )}
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </div>
                    </div>
                  </div>

                  {/* Détails expandés */}
                  {isExpanded && (
                    <div className="p-3 bg-white border-t space-y-3">
                      {/* Infos détaillées */}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">Créé le:</span>
                          <p className="font-medium">
                            {new Date(incident.created_at).toLocaleString('fr-FR')}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">Par:</span>
                          <p className="font-medium">{incident.created_by}</p>
                        </div>
                        {incident.location && (
                          <div className="col-span-2">
                            <span className="text-gray-500 flex items-center gap-1">
                              <MapPin className="h-3 w-3" /> Lieu:
                            </span>
                            <p className="font-medium">{incident.location}</p>
                          </div>
                        )}
                      </div>

                      {/* Cause complète */}
                      <div>
                        <span className="text-xs text-gray-500">Cause:</span>
                        <p className="text-sm bg-gray-50 p-2 rounded">{incident.cause}</p>
                      </div>

                      {/* Actions de statut */}
                      {incident.status !== 'clos' && (
                        <div className="flex gap-2">
                          {incident.status === 'ouvert' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => updateIncidentStatus(incident.incident_id, 'en_cours')}
                            >
                              Prendre en charge
                            </Button>
                          )}
                          {incident.status === 'en_cours' && (
                            <Button 
                              size="sm"
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => updateIncidentStatus(incident.incident_id, 'resolu')}
                            >
                              Marquer résolu
                            </Button>
                          )}
                          {incident.status === 'resolu' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => updateIncidentStatus(incident.incident_id, 'clos')}
                            >
                              Clore l&apos;incident
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Commentaires */}
                      {incident.comments?.length > 0 && (
                        <div>
                          <Separator className="my-2" />
                          <span className="text-xs text-gray-500 flex items-center gap-1 mb-2">
                            <MessageSquare className="h-3 w-3" />
                            Commentaires ({incident.comments.length})
                          </span>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {incident.comments.map((comment, idx) => (
                              <div key={idx} className="bg-gray-50 p-2 rounded text-xs">
                                <div className="flex justify-between text-gray-500 mb-1">
                                  <span className="font-medium">{comment.author_name || comment.author}</span>
                                  <span>{new Date(comment.created_at).toLocaleString('fr-FR')}</span>
                                </div>
                                <p>{comment.content}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Ajouter un commentaire */}
                      {incident.status !== 'clos' && (
                        <div className="flex gap-2">
                          <Input
                            value={expandedIncident === incident.incident_id ? newComment : ''}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Ajouter un commentaire..."
                            className="h-8 text-sm"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                addComment(incident.incident_id);
                              }
                            }}
                          />
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => addComment(incident.incident_id)}
                            disabled={!newComment.trim()}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IncidentsPanel;
